import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { collection, getDocs, addDoc, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { uploadImage } from '../../utils/cloudinary';
import { FaEdit, FaTrash, FaPlus, FaSave, FaTimes } from 'react-icons/fa';

// Styled Components


const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const GalleryItem = styled.div`
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  position: relative;
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
`;

// const PreviewImage = styled.img`
//   width: 100%;
//   height: 200px;
//   object-fit: cover;
//   border-radius: 0.5rem;
// `;

const GalleryActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const GalleryInfo = styled.div`
  padding: 1rem;
  background-color: #f8f9fa;
`;

const GalleryTitle = styled.h3`
  font-size: 1rem;
  margin-bottom: 0.5rem;
`;

const GalleryCategory = styled.p`
  font-size: 0.9rem;
  color: #6c757d;
  margin-bottom: 0;
`;

// const ActionButtons = styled.div`
//   position: absolute;
//   top: 10px;
//   right: 10px;
//   display: flex;
//   gap: 0.5rem;
// `;

const ActionButton = styled.button`
  background-color: rgba(255, 255, 255, 0.8);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: white;
    transform: scale(1.1);
  }
`;

const AddButton = styled.button`
display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: #1e3a8a;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 2rem;
  
  &:hover {
    background-color: #1c3175;
`;

const FormOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Form = styled.form`
  background-color: white;
  border-radius: 8px;
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
`;

const FormTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: #1e3a8a;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 1rem;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 1rem;
`;

const ImagePreview = styled.div`
  margin-top: 1rem;
  
  img {
    max-width: 100%;
    max-height: 200px;
    border-radius: 4px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
`;

const CancelButton = styled(Button)`
  background-color: #e9ecef;
  color: #495057;
  
  &:hover {
    background-color: #dee2e6;
  }
`;

const SaveButton = styled(Button)`
  background-color: #1e3a8a;
  color: white;
  
  &:hover {
    background-color: #1e40af;
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const LoadingSpinner = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid #1e3a8a;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Message = styled.div`
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 4px;
  background-color: ${props => props.type === 'success' ? '#d4edda' : '#f8d7da'};
  color: ${props => props.type === 'success' ? '#155724' : '#721c24'};
`;

const GalleryManagement = () => {
  const [galleryImages, setGalleryImages] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    category: 'Rooms',
    imageFile: null,
    imageUrl: ''
  });
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  // Available categories
  const categories = ['Rooms', 'Dining', 'Views', 'Events'];

  // Fetch gallery images from Firebase
  useEffect(() => {
    fetchGalleryImages();
  }, []);

  const fetchGalleryImages = async () => {
    setLoading(true);
    try {
      const galleryCollection = collection(db, 'galleryImages');
      const gallerySnapshot = await getDocs(galleryCollection);
      const galleryList = gallerySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setGalleryImages(galleryList);
    } catch (error) {
      console.error('Error fetching gallery images:', error);
      setMessage({ text: 'Failed to load gallery images.', type: 'error' });
    }
    setLoading(false);
  };

  const handleAddClick = () => {
    setEditingImage(null);
    setFormData({
      title: '',
      category: 'Rooms',
      imageFile: null,
      imageUrl: ''
    });
    setPreviewUrl('');
    setShowForm(true);
  };

  const handleEditClick = (image) => {
    setEditingImage(image);
    setFormData({
      title: image.title,
      category: image.category,
      imageFile: null,
      imageUrl: image.url
    });
    setPreviewUrl(image.url);
    setShowForm(true);
  };

  const handleDeleteClick = async (id) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      setLoading(true);
      try {
        await deleteDoc(doc(db, 'galleryImages', id));
        setMessage({ text: 'Image deleted successfully!', type: 'success' });
        fetchGalleryImages();
      } catch (error) {
        console.error('Error deleting image:', error);
        setMessage({ text: 'Failed to delete image.', type: 'error' });
        setLoading(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        imageFile: file
      });
      
      // Create a preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      let imageUrl = formData.imageUrl;
      
      // Upload image to Cloudinary if a new file is selected
      if (formData.imageFile) {
        const uploadResult = await uploadImage(formData.imageFile);
        imageUrl = uploadResult.url; // Fix: access the correct property name
      }
      
      const galleryData = {
        title: formData.title,
        category: formData.category,
        url: imageUrl
      };
      
      if (editingImage) {
        // Update existing image
        await updateDoc(doc(db, 'galleryImages', editingImage.id), galleryData);
        setMessage({ text: 'Gallery image updated successfully!', type: 'success' });
      } else {
        // Add new image
        await addDoc(collection(db, 'galleryImages'), galleryData);
        setMessage({ text: 'New gallery image added successfully!', type: 'success' });
      }
      
      setShowForm(false);
      fetchGalleryImages();
    } catch (error) {
      console.error('Error saving gallery image:', error);
      setMessage({ text: 'Failed to save gallery image.', type: 'error' });
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
  };

  return (
    <>
      {message.text && (
        <Message type={message.type}>{message.text}</Message>
      )}
      
      <AddButton onClick={handleAddClick}>
        <FaPlus /> Add New Gallery Image
      </AddButton>
      
      <GalleryGrid>
        {galleryImages.map((image) => (
          <GalleryItem key={image.id}>
            <GalleryImage src={image.url} alt={image.title} />
            <GalleryInfo>
              <GalleryTitle>{image.title}</GalleryTitle>
              <GalleryCategory>{image.category}</GalleryCategory>
              <GalleryActions>
                <ActionButton className="edit" onClick={() => handleEditClick(image)}>
                  <FaEdit />
                </ActionButton>
                <ActionButton className="delete" onClick={() => handleDeleteClick(image.id)}>
                  <FaTrash />
                </ActionButton>
              </GalleryActions>
            </GalleryInfo>
          </GalleryItem>
        ))}
      </GalleryGrid>
      
      {showForm && (
        <FormOverlay>
          <Form onSubmit={handleSubmit}>
            <FormTitle>
              {editingImage ? 'Edit Gallery Image' : 'Add New Gallery Image'}
            </FormTitle>
            
            <FormGroup>
              <Label htmlFor="title">Title</Label>
              <Input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="category">Category</Label>
              <Select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </Select>
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="image">Image</Label>
              <Input
                type="file"
                id="image"
                name="image"
                accept="image/*"
                onChange={handleImageChange}
                required={!editingImage}
              />
              
              {previewUrl && (
                <ImagePreview>
                  <img src={previewUrl} alt="Preview" />
                </ImagePreview>
              )}
            </FormGroup>
            
            <ButtonGroup>
              <CancelButton type="button" onClick={handleCancel}>
                <FaTimes /> Cancel
              </CancelButton>
              <SaveButton type="submit">
                <FaSave /> Save
              </SaveButton>
            </ButtonGroup>
          </Form>
        </FormOverlay>
      )}
      
      {loading && (
        <LoadingOverlay>
          <LoadingSpinner />
        </LoadingOverlay>
      )}
    </>
  );
};

export default GalleryManagement;
